
import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, X, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { BUILD_INFO, FRONTEND_BOOT } from '../buildInfo';
import { AuditCallMeta } from './probateApi';
import { getLastAuditMeta, AuditDebugMeta, setLastAuditMeta, newAuditId, getLastBackendFingerprint, BackendFingerprint } from './probateDebugStore';
import { emitProbateScroll } from './probate/scrollTargets';

interface Props {
 isOpen: boolean;
 onClose: () => void;
 result: any;
 lastAuditMeta?: AuditCallMeta | null;
 runDebug?: { clickedAt?: string; stage?: string; error?: string } | null;
 sessionNonce: string;
 componentName?: string;
 goToStep?: (stepId: string) => boolean;
}

interface BackendStatus {
 connected: boolean;
 loading: boolean;
 health: any;
 buildInfo: any;
 fileProbes: any;
 error: {
 message: string;
 status?: number;
 url: string;
 } | null;
}

export const DebugPanel: React.FC<Props> = ({
 isOpen,
 onClose,
 result,
 lastAuditMeta,
 runDebug,
 sessionNonce,
 componentName ="Unknown",
 goToStep
}) => {
 const [auditMeta, setAuditMeta] = useState<AuditDebugMeta | null>(null);
 const [backendFingerprint, setBackendFingerprint] = useState<BackendFingerprint | null>(null);
 const [backend, setBackend] = useState<BackendStatus>({
 connected: false,
 loading: false,
 health: null,
 buildInfo: null,
 fileProbes: null,
 error: null
 });

 const handleTestScroll = () => {
 // 1. Try to navigate to properties first if we can
 // We assume 'property' step exists or 'review' step contains properties.
 // Actually, for Review step, we shouldn't nav away if we are already there.
 // But the user might be on Welcome step.

 let navigated = false;
 if (goToStep) {
 // Try to go to property step directly? Or Review?
 // If the user wants to test"Go to Properties", let's try 'review' as that's where the EstateBuilder is often tested now,
 // OR 'property' if it exists.

 // Actually, the button says"Test Scroll to Properties".
 // If we are in Review, we want to scroll anchors.
 // If we are elsewhere, we might want to jump to properties.
 // Let's try navigating to 'review' first as it's the safest place where EstateBuilder exists in this flow?
 // Wait, standard flow has 'property' step IF selected. Review has it always.
 // Let's try 'review' to be safe.
 navigated = goToStep('review');
 }

 // 2. Emit scroll event (with small delay if we navigated to allow mount)
 if (navigated) {
 setTimeout(() => {
 emitProbateScroll({ target: 'properties', focus: 'main_residence' });
 }, 500);
 } else {
 emitProbateScroll({ target: 'properties', focus: 'main_residence' });
 }
 };

 const fetchBackendStatus = useCallback(async () => {
 setBackend(prev => ({ ...prev, loading: true, error: null }));

 // Use relative URLs - they go through Vite proxy
 const healthUrl = `/api/debug/health?t=${Date.now()}`;
 const fileProbesUrl = `/api/debug/file-probes?t=${Date.now()}`;

 try {
 const healthRes = await fetch(healthUrl, { cache: 'no-store' });

 if (!healthRes.ok) {
 throw new Error(`HTTP ${healthRes.status}: ${healthRes.statusText} `);
 }

 const healthData = await healthRes.json();

 // Fetch file probes (dev only)
 let fileProbesData = null;
 try {
 const fileProbesRes = await fetch(fileProbesUrl, { cache: 'no-store' });
 if (fileProbesRes.ok) {
 fileProbesData = await fileProbesRes.json();
 }
 } catch {
 // File probes are optional
 }

 setBackend({
 connected: true,
 loading: false,
 health: healthData,
 buildInfo: healthData,
 fileProbes: fileProbesData,
 error: null
 });

 } catch (err: any) {
 let errorMessage = 'Unknown error';
 let status: number | undefined;

 if (err instanceof TypeError && err.message.includes('fetch')) {
 errorMessage = 'Network error - server not reachable';
 } else if (err.message) {
 errorMessage = err.message;
 const match = err.message.match(/HTTP (\d+)/);
 if (match) status = parseInt(match[1], 10);
 }

 setBackend({
 connected: false,
 loading: false,
 health: null,
 buildInfo: null,
 fileProbes: null,
 error: {
 message: errorMessage,
 status,
 url: healthUrl
 }
 });
 }
 }, []);

 useEffect(() => {
 if (isOpen) {
 fetchBackendStatus();
 }
 }, [isOpen, fetchBackendStatus]);

 // Stale State Clearing Logic
 useEffect(() => {
 if (backend.health?.started_at_iso && backendFingerprint?.startedAt) {
 const healthStart = new Date(backend.health.started_at_iso).getTime();
 const lastAuditStart = new Date(backendFingerprint.startedAt).getTime();
 // Allow small drift (e.g. 2s) or strict equality? ISO strings should be identical if from same boot.
 // Let's use strict string comparison to be safe, or allow 1s diff.
 // Actually, if PIDs differ, that's the real test.
 const pidMismatch = String(backend.health.pid) !== String(backendFingerprint.pid);

 if (pidMismatch && auditMeta) {
 console.warn("[DebugPanel] Backend restart detected. Clearing stale audit state.");
 setLastAuditMeta(null); // Clear the echo
 // We don't clear fingerprint itself, so we can still show the mismatch warning
 }
 }
 }, [backend.health, backendFingerprint, auditMeta]);



 useEffect(() => {
 if (!isOpen) return;
 const tick = () => {
 setAuditMeta(getLastAuditMeta());
 setBackendFingerprint(getLastBackendFingerprint());
 };
 tick();
 const t = setInterval(tick, 500);
 return () => clearInterval(t);
 }, [isOpen]);

 if (!isOpen) return null;

 const hasStaleWarning = backend.fileProbes?.probes &&
 Object.values(backend.fileProbes.probes).some((p: any) => !p.exists);

 const formatMtime = (mtimeMs: number | null): string => {
 if (mtimeMs === null) return 'N/A';
 try {
 return new Date(mtimeMs).toLocaleString();
 } catch {
 return 'Invalid';
 }
 };

 const formatUptime = (seconds: number | undefined | null): string => {
 if (seconds === undefined || seconds === null) return 'N/A';
 if (seconds < 60) return `${seconds} s`;
 if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60} s`;
 const h = Math.floor(seconds / 3600);
 const m = Math.floor((seconds % 3600) / 60);
 return `${h}h ${m} m`;
 };

 return (
 <div className="fixed inset-0 z-[110] bg-[#1A1A1A]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
 <div className="bg-white rounded w-full max-w-2xl overflow-hidden flex flex-col h-[80vh]" onClick={e => e.stopPropagation()}>
 <div className="px-6 py-4 bg-[#FAFAF8] border-b flex justify-between items-center">
 <h3 className="font-bold text-[#1A1A1A] flex items-center gap-2 italic">
 <AlertTriangle className="w-4 h-4 text-[#7A5A00]" />
 Investigation Mode
 </h3>
 <div className="flex items-center gap-2">
 <button
 onClick={handleTestScroll}
 className="mr-2 px-2 py-1 bg-[#EEF0FA] hover:bg-[#EEF0FA] text-[#3730A3] border border-[#C7CBEA] rounded text-[10px] font-bold transition-colors"
 title="Test Scroll to Properties (Forces Nav to Review)"
 >
 Test Scroll
 </button>
 <button
 onClick={fetchBackendStatus}
 className="p-2 text-[#AFA99F] hover:text-[#3F3D39] hover:bg-[#FAFAF8] rounded"
 title="Refresh backend status"
 >
 <RefreshCw className={`w - 4 h - 4 ${backend.loading ? 'animate-spin' : ''} `} />
 </button>
 <button onClick={onClose} className="p-2 text-[#AFA99F] hover:text-[#3F3D39]"><X /></button>
 </div>
 </div>

 <div className="flex-1 overflow-auto p-6 space-y-6 text-left">
 {hasStaleWarning && (
 <div className="p-3 bg-[#FAF3E0] border border-[#E8D89A] rounded text-xs text-[#7A5A00] font-bold flex items-center gap-2">
 <AlertTriangle className="w-4 h-4" />
 Backend cannot see some files — might be running from a different workspace.
 </div>
 )}

 <div className="grid grid-cols-2 gap-6">
 {/* Frontend Build logic replaced with Fingerprints */}
 <div className="col-span-2 space-y-4">
 <h4 className="text-[10px] font-bold text-[#AFA99F] uppercase tracking-widest border-b border-[#E8E4DB] pb-1">
 Build Fingerprints (Proof of Life)
 </h4>

 <div className="grid grid-cols-2 gap-4">
 {/* Frontend Identity */}
 <div className="p-3 bg-[#EEF0FA]/50 rounded border border-[#C7CBEA] text-[11px] font-mono space-y-1">
 <div className="font-bold text-[#3730A3] mb-1">Frontend Identity</div>
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Boot Nonce:</span>
 <span className="font-bold text-[#3F3D39]">{FRONTEND_BOOT.nonce}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Boot Time:</span>
 <span className="text-[#3F3D39]">{new Date(FRONTEND_BOOT.bootTime).toLocaleTimeString()}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Build Time:</span>
 <span className="text-[#3F3D39]">{BUILD_INFO.buildTime || 'dev-mode'}</span>
 </div>
 </div>
 {/* Route Context */}
 <div className="mt-2 text-[10px] flex gap-2 col-span-2">
 <div className="bg-[#FAFAF8] rounded px-2 py-1 text-[#6B6762] border border-[#E8E4DB]">
 <span className="font-bold">Route:</span> {window.location.pathname}
 </div>
 <div className="bg-[#FAFAF8] rounded px-2 py-1 text-[#6B6762] border border-[#E8E4DB]">
 <span className="font-bold">Comp:</span> {componentName}
 </div>
 </div>

 {/* Backend Identity (from Audit Headers) */}
 <div className="p-3 bg-[#EEF0FA]/50 rounded border border-[#C7CBEA] text-[11px] font-mono space-y-1 relative">
 <div className="font-bold text-[#3730A3] mb-1">Backend Identity (Last Audit)</div>
 {backendFingerprint ? (
 <>
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">PID:</span>
 <span className="font-bold text-[#3F3D39]">{backendFingerprint.pid}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Started:</span>
 <span className="text-[#3F3D39]">{backendFingerprint.startedAt ? new Date(backendFingerprint.startedAt).toLocaleTimeString() : 'N/A'}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Uptime:</span>
 <span className="text-[#3F3D39]">{Math.floor(Number(backendFingerprint.uptimeMs) / 1000)}s</span>
 </div>
 {backendFingerprint.runId && (
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Run ID:</span>
 <span className="text-[#3F3D39] text-[9px]">{backendFingerprint.runId.split('-').pop()}</span>
 </div>
 )}
 </>
 ) : (
 <div className="text-[#AFA99F] italic py-2">No audit headers captured yet</div>
 )}

 {/* Mismatch Warning */}
 {backendFingerprint && backend.health && backendFingerprint.pid !== String(backend.health.pid) && (
 <div className="absolute inset-x-0 -bottom-2 translate-y-full bg-white text-[#1A1A1A] text-[10px] p-2 rounded border border-[#E8E4DB] mt-2 z-10">
 <strong className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> MISMATCH WARNING</strong>
 Audit headers PID ({backendFingerprint.pid}) != Health PID ({backend.health.pid}).
 Backend restart detected?
 </div>
 )}
 </div>
 </div>
 </div>

 {/* Backend Build */}
 <div className="space-y-3">
 <h4 className="text-[10px] font-bold text-[#AFA99F] uppercase tracking-widest">Backend Build</h4>
 <div className="p-3 bg-[#FAFAF8] rounded border border-[#E8E4DB] text-[11px] font-mono space-y-1 min-h-[60px]">
 {backend.loading ? (
 <div className="text-[#AFA99F] italic flex items-center gap-2">
 <RefreshCw className="w-3 h-3 animate-spin" />
 Fetching...
 </div>
 ) : backend.connected && backend.health ? (
 <>
 <div className="flex items-center gap-1 text-[#1A1A1A] font-bold mb-2">
 <CheckCircle className="w-3 h-3" />
 Connected
 </div>
 <div><span className="text-[#AFA99F]">pid:</span> {backend.health.pid}</div>
 <div><span className="text-[#AFA99F]">uptime:</span> {formatUptime(backend.health.uptime_s)}</div>
 <div><span className="text-[#AFA99F]">env:</span> {backend.health.env || 'null'}</div>
 </>
 ) : (
 <>
 <div className="flex items-center gap-1 text-[#1A1A1A] font-bold mb-2">
 <XCircle className="w-3 h-3" />
 Disconnected
 </div>
 {backend.error && (
 <>
 <div className="text-[#1A1A1A]">
 <span className="text-[#AFA99F]">Error:</span> {backend.error.message}
 </div>
 <div className="text-[10px] text-[#AFA99F] break-all">
 URL: {backend.error.url}
 </div>
 <div className="mt-2 p-2 bg-[#FAF3E0] border border-[#E8D89A] rounded text-[10px] text-[#7A5A00]">
 <strong>Hint:</strong> {backend.error.status === undefined
 ? 'Server not reachable. Is the backend running?'
 : `Server returned HTTP ${backend.error.status} `}
 </div>
 </>
 )}
 </>
 )}
 </div>
 </div>
 </div>

 {/* File Probes */}
 <div className="space-y-3">
 <h4 className="text-[10px] font-bold text-[#AFA99F] uppercase tracking-widest">Backend File Probes (mtime)</h4>
 <div className="p-3 bg-[#FAFAF8] rounded border border-[#E8E4DB] text-[11px] font-mono space-y-1">
 {backend.fileProbes?.probes ? (
 Object.entries(backend.fileProbes.probes).map(([path, probe]: [string, any]) => (
 <div key={path} className="flex justify-between">
 <span className="text-[#AFA99F]">{path.split('/').pop()}:</span>
 <span className={probe.exists ? 'text-[#3F3D39]' : 'text-[#6B6762]'}>
 {probe.exists ? formatMtime(probe.mtimeMs) : 'Not found'}
 </span>
 </div>
 ))
 ) : backend.connected ? (
 <div className="text-[#AFA99F] italic">File probes not available</div>
 ) : (
 <div className="text-[#AFA99F] italic">Backend not connected</div>
 )}
 </div>
 </div>

 {/* Last Audit Call */}
 <div className="space-y-3">
 <h4 className="text-[10px] font-bold text-[#AFA99F] uppercase tracking-widest">Last Audit Call</h4>
 <div className="p-3 bg-[#FAFAF8] rounded border border-[#E8E4DB] text-[11px] font-mono space-y-1">
 {auditMeta ? (
 <>
 <div className="border-b border-[#E8E4DB] pb-2 mb-2">
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">ID:</span>
 <span className="text-[#3F3D39] break-all text-[10px]">{auditMeta.id}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Stage:</span>
 <span className={`font - bold ${auditMeta.stage === 'response_received' ? 'text-[#1A1A1A]' :
 auditMeta.stage?.includes('invalid') || auditMeta.stage?.includes('failed') ? 'text-[#1A1A1A]' : 'text-[#3730A3]'
 } `}>
 {auditMeta.stage}
 </span>
 </div>
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">URL:</span>
 <span className="text-[#3F3D39] break-all text-right max-w-[200px]">{auditMeta.url}</span>
 </div>
 </div>

 <div className="space-y-1">
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Time:</span>
 <span className="text-[#3F3D39]">{new Date(auditMeta.startedAt).toLocaleTimeString()}</span>
 </div>
 {auditMeta.status !== undefined && (
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Status:</span>
 <span className={auditMeta.ok ? 'text-[#1A1A1A] font-bold' : 'text-[#1A1A1A] font-bold'}>
 {auditMeta.status} {auditMeta.ok ? '✓' : '✗'}
 </span>
 </div>
 )}
 {auditMeta.durationMs !== undefined && (
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Duration:</span>
 <span className="text-[#3F3D39]">{auditMeta.durationMs}ms</span>
 </div>
 )}
 {auditMeta.error && (
 <div className="mt-2 p-2 bg-white border border-[#E8E4DB] rounded text-[#1A1A1A] text-[10px] break-all">
 <span className="font-bold">Error:</span> {auditMeta.error}
 </div>
 )}
 {auditMeta.responseSummary && (
 <div className="border-t border-[#E8E4DB] mt-2 pt-2 space-y-1">
 <div className="font-bold text-[#6B6762] mb-1 text-[10px] uppercase">Server Response Echo</div>
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Regime:</span>
 <span className="text-[#3F3D39] font-mono">{auditMeta.responseSummary.regime || '—'}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">Snap. Status:</span>
 <span className="text-[#3F3D39] font-bold">{auditMeta.responseSummary.snapshot_status || '—'}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-[#AFA99F]">IHT Status:</span>
 <span className="text-[#3F3D39]">{auditMeta.responseSummary.iht_status || '—'}</span>
 </div>
 {auditMeta.responseSummary.missing && auditMeta.responseSummary.missing.length > 0 && (
 <div className="mt-1">
 <div className="text-[#AFA99F]">Missing/Blocking ({auditMeta.responseSummary.missing.length}):</div>
 <div className="text-[10px] text-[#1A1A1A] font-mono break-all leading-tight mt-1 bg-white p-1 rounded border border-[#E8E4DB]">
 {auditMeta.responseSummary.missing.join(",")}
 </div>
 </div>
 )}
 {auditMeta.responseSummary.decisions && (
 <div className="mt-1">
 <div className="text-[#AFA99F]">Decision keys ({Object.keys(auditMeta.responseSummary.decisions).length}):</div>
 <div className="text-[10px] text-[#6B6762] font-mono break-all leading-tight">
 {Object.keys(auditMeta.responseSummary.decisions).length > 0
 ? Object.keys(auditMeta.responseSummary.decisions).slice(0, 12).join(",") + (Object.keys(auditMeta.responseSummary.decisions).length > 12 ?"..." :"")
 : 'None'}
 </div>
 </div>
 )}
 </div>
 )}
 <div className="border-t border-[#E8E4DB] mt-2 pt-2">
 <span className="text-[#AFA99F]">Local UI Prop:</span> {result?.snapshot?.status || 'N/A'}
 </div>
 </div>
 </>
 ) : (
 <div className="text-[#AFA99F] italic">No audit calls yet</div>
 )}
 </div>
 </div>

 {/* Force Refresh Guide */}
 <div className="p-4 bg-[#1A1A1A] rounded-lg text-[10px] text-[#AFA99F] space-y-2">
 <p className="font-bold text-[#AFA99F] text-xs">Force Refresh Guide:</p>
 <p>Windows: <kbd className="bg-[#3F3D39] px-1 rounded text-white">Ctrl + F5</kbd></p>
 <p>Mac: <kbd className="bg-[#3F3D39] px-1 rounded text-white">Cmd + Shift + R</kbd></p>
 <p>Incognito/Private mode is recommended if updates still don't appear.</p>
 </div>
 </div>

 <div className="p-4 bg-[#FAFAF8] border-t flex justify-center">
 <p className="text-[10px] text-[#AFA99F] text-center italic">
 If session nonce stays same after refresh, try browser hard-refresh (Ctrl+F5).
 </p>
 </div>
 </div>
 </div>
 );
};
